import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

// eslint-disable-next-line import/no-cycle
import { updateRuntimeBranding } from '../config/branding';
import brandingService, { BrandingData } from '../utils/api/brandingService';
import { generateFullTheme } from '../utils/brandingUtils';

interface BrandingContextType {
  brandData: BrandingData | null;
  isLoading: boolean;
  error: string | null;
  refreshBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

/**
 * Injects theme CSS variables into the document root.
 * @param themeConfig - The theme configuration object containing color definitions.
 */
const applyThemeToDocument = (themeConfig: any) => {
  if (!themeConfig) return;

  // Generate full theme variables (light and dark)
  const fullTheme = generateFullTheme(themeConfig);

  // Determine current mode (default to light or get from localStorage)
  const currentMode = localStorage.getItem('globalTheme') || 'light';
  const themeVariables = fullTheme[currentMode] || fullTheme.light;

  if (themeVariables) {
    Object.entries(themeVariables).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value as string);
    });
  }
};

/**
 * Updates document metadata like title and favicon based on branding data.
 * @param data - The branding data.
 */
const updateDocumentMetadata = (data: BrandingData) => {
  if (data.clientName) {
    document.title = data.clientName;
  }

  if (data.favicon) {
    const link = document.querySelector("link[rel*='icon']");
    if (link instanceof HTMLLinkElement) {
      link.href = data.favicon;
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = data.favicon;
      document.head.appendChild(newLink);
    }
  } else {
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.href = '/favicon.ico';
    document.head.appendChild(newLink);
  }

  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription && data.clientName) {
    metaDescription.setAttribute('content', data.clientName);
  }
};

/**
 * BrandingProvider component that fetches and manages runtime branding data.
 * @param props - Component properties.
 * @param props.children - Child elements.
 * @returns The provider component.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  // Always start with null — getCachedBranding validates the domain, so we
  // let fetchBranding populate state with either fresh API data or a valid same-domain cache.
  const [brandData, setBrandData] = useState<BrandingData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches branding data from the API and updates state.
   */
  const fetchBranding = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await brandingService.fetchBranding();
      if (data) {
        setBrandData(data);
        updateRuntimeBranding(data);
        applyThemeToDocument(data.theme);
        updateDocumentMetadata(data);
      } else {
        // No data for current domain — reset favicon to default
        brandingService.resetFavicon();
      }
      setError(null);
    } catch (error_) {
      brandingService.resetFavicon();
      setError('Failed to load branding');
      console.error(error_);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  useEffect(() => {
    /**
     * Handles storage changes to re-apply themes if the global mode changes.
     * @param event - The storage event.
     */
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'globalTheme' && brandData?.theme) {
        applyThemeToDocument(brandData.theme);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [brandData?.theme]);

  // Re-apply theme if brandData changes
  useEffect(() => {
    if (brandData?.theme) {
      applyThemeToDocument(brandData.theme);
    }
  }, [brandData]);

  const contextValue = useMemo(
    () => ({
      brandData,
      isLoading,
      error,
      refreshBranding: fetchBranding,
    }),
    [brandData, isLoading, error, fetchBranding],
  );

  // If still loading and we don't have cached data yet, show a simple loader to avoid default branding flash
  if (isLoading && !brandData) {
    return (
      <div
        style={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#f8f9fa',
        }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden" />
          </div>
        </div>
      </div>
    );
  }

  return <BrandingContext.Provider value={contextValue}>{children}</BrandingContext.Provider>;
}

/**
 * Custom hook to consume branding data from the BrandingContext.
 * @returns The branding context data.
 */
export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
